import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, For, Show } from "solid-js";
import { JourneyPageCard } from "./JourneyPageCard";
import type {
  CandidateJourney,
  PriorityContentFlow,
  PriorityFlowPreview,
} from "./journeyTypes";
import { compactRouteLabel, uniqueRouteLabels } from "./priorityFlowLabels";
import { clusterContextForPages } from "./scanModel";

export function JourneyExplorer(props: {
  data: OverviewResponse;
  flows: PriorityFlowPreview;
  selectedFlow: PriorityContentFlow | null;
  selectedPath: CandidateJourney | null;
  selectedPageId: string | null;
  emptyReason: string | null;
  onSelectFlow: (flowId: string) => void;
  onSelectPath: (pathId: string) => void;
  onSelectPage: (pageId: string) => void;
}) {
  return (
    <section class="flow-path-focus" aria-label="Current Paths">
      <div class="flow-path-focus-head">
        <div>
          <span class="signal-mini-kicker">Current Paths</span>
          <h3>
            Review the most important inferred paths through the current estate
          </h3>
        </div>
        <span>{topLevelCountLabel(props.flows)}</span>
      </div>
      <Show
        when={props.flows.topLevel.length > 0}
        fallback={
          <div class="journey-empty-state">
            <strong>No current path available</strong>
            <p>{props.emptyReason}</p>
          </div>
        }
      >
        <fieldset class="priority-flow-selector" aria-label="Current Paths">
          <For each={props.flows.topLevel}>
            {(flow) => (
              <PriorityFlowButton
                flow={flow}
                selected={props.selectedFlow?.id === flow.id}
                onSelect={() => props.onSelectFlow(flow.id)}
              />
            )}
          </For>
        </fieldset>

        <Show when={props.flows.other.length > 0}>
          <details class="other-flows-disclosure">
            <summary>
              Other current paths
              <span>{props.flows.other.length}</span>
            </summary>
            <div class="other-flow-list">
              <For each={props.flows.other}>
                {(flow) => (
                  <button
                    type="button"
                    class={
                      props.selectedFlow?.id === flow.id ? "is-selected" : ""
                    }
                    aria-pressed={props.selectedFlow?.id === flow.id}
                    onClick={() => props.onSelectFlow(flow.id)}
                  >
                    <strong>{flow.label}</strong>
                    <small>{flow.priorityReason}</small>
                    <span>{flow.paths.length} routes</span>
                  </button>
                )}
              </For>
            </div>
          </details>
        </Show>

        <Show when={props.selectedFlow} keyed>
          {(flow) => (
            <Show when={props.selectedPath} keyed>
              {(path) => (
                <SelectedRoute
                  data={props.data}
                  flow={flow}
                  path={path}
                  selectedPageId={props.selectedPageId}
                  onSelectPath={props.onSelectPath}
                  onSelectPage={props.onSelectPage}
                />
              )}
            </Show>
          )}
        </Show>
      </Show>
    </section>
  );
}

function PriorityFlowButton(props: {
  flow: PriorityContentFlow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      class={props.selected ? "is-selected" : ""}
      aria-pressed={props.selected}
      onClick={props.onSelect}
    >
      <span class={`priority-flow-tier is-${props.flow.priorityTier}`}>
        {props.flow.neutralRepresentative
          ? "Representative path"
          : props.flow.priorityTier}
      </span>
      <strong>{props.flow.label}</strong>
      <p>{props.flow.priorityReason}</p>
      <small>
        {props.flow.uniquePages.length} pages / {props.flow.paths.length}{" "}
        {props.flow.paths.length === 1 ? "route" : "routes"} /{" "}
        {props.flow.priorityConcernCount}{" "}
        {props.flow.priorityConcernCount === 1 ? "concern" : "concerns"}
      </small>
    </button>
  );
}

function SelectedRoute(props: {
  data: OverviewResponse;
  flow: PriorityContentFlow;
  path: CandidateJourney;
  selectedPageId: string | null;
  onSelectPath: (pathId: string) => void;
  onSelectPage: (pageId: string) => void;
}) {
  const clusterContext = createMemo(() =>
    clusterContextForPages(props.data.pages),
  );
  const routeOptions = () => [
    ...new Map(
      [props.path, ...props.flow.paths].map((path) => [path.id, path] as const),
    ).values(),
  ];
  const routeLabels = () =>
    uniqueRouteLabels(routeOptions(), props.data.site, clusterContext());
  return (
    <div class="selected-flow-route">
      <div class="selected-flow-route-head">
        <div>
          <span class="signal-mini-kicker">
            Representative structural route
          </span>
          <h4>
            {routeLabels().get(props.path.id) ??
              compactRouteLabel(props.path, props.data.site, clusterContext())}
          </h4>
        </div>
        <span>
          {props.path.assessedPages}/{props.path.steps.length} pages assessed
        </span>
      </div>
      <Show when={routeOptions().length > 1}>
        <details class="related-route-disclosure">
          <summary>{routeOptions().length - 1} related routes</summary>
          <fieldset aria-label={`Routes in ${props.flow.label}`}>
            <For each={routeOptions()}>
              {(path) => (
                <button
                  type="button"
                  class={props.path.id === path.id ? "is-selected" : ""}
                  aria-pressed={props.path.id === path.id}
                  onClick={() => props.onSelectPath(path.id)}
                >
                  {routeLabels().get(path.id) ??
                    compactRouteLabel(path, props.data.site, clusterContext())}
                </button>
              )}
            </For>
          </fieldset>
        </details>
      </Show>
      <ol
        class="flow-path-steps"
        aria-label={`Selected route: ${
          routeLabels().get(props.path.id) ??
          compactRouteLabel(props.path, props.data.site, clusterContext())
        }`}
      >
        <For each={props.path.steps}>
          {(step, index) => (
            <li>
              <span class="flow-path-step-number">{index() + 1}</span>
              <JourneyPageCard
                step={step}
                site={props.data.site}
                selected={props.selectedPageId === step.page.id}
                onSelect={() => props.onSelectPage(step.page.id)}
              />
            </li>
          )}
        </For>
      </ol>
    </div>
  );
}

function topLevelCountLabel(flows: PriorityFlowPreview): string {
  const count = flows.topLevel.length;
  if (flows.topLevel[0]?.neutralRepresentative) {
    return "1 representative path";
  }
  return `${count} prioritised ${count === 1 ? "path" : "paths"}`;
}
